import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map, distinctUntilChanged, shareReplay } from 'rxjs/operators';

export interface AppLoadingState {
  isInitializing: boolean;
  servicesLoaded: boolean;
  dataEnriched: boolean;
  readyToDisplay: boolean;
}

export interface ServiceLoadingState {
  properties: boolean;
  propertyGroups: boolean;
  contracts: boolean;
  loans: boolean;
  tenants: boolean;
  expenses: boolean;
  globalIndex: boolean;
  financialCalculations: boolean;
}

export interface DataCoordinationMetrics {
  totalServices: number;
  loadedServices: number;
  failedServices: string[];
  loadingStartTime: number;
  loadingDuration?: number;
  cacheHitRate?: number;
}

/**
 * @domain Core/Services
 * 
 * Unified Data Coordination Service - Comprehensive coordination of all data loading and state management
 * 
 * This service:
 * - Coordinates between all loading services (UnifiedLoadingService, PropertyListStateService, etc.)
 * - Prevents UI updates during initial loading phases
 * - Ensures smooth, single-load user experience
 * - Eliminates screen flickering and multiple reloads
 * - Provides centralized metrics and monitoring
 * - Manages service dependencies and load order
 * - Implements intelligent caching coordination
 * 
 * Replaces the need for multiple competing loading services
 */
@Injectable({
  providedIn: 'root'
})
export class AppCoordinationService {
  private isInitializingSubject = new BehaviorSubject<boolean>(true);
  private servicesLoadedSubject = new BehaviorSubject<boolean>(false);
  private dataEnrichedSubject = new BehaviorSubject<boolean>(false);
  
  // Enhanced service tracking
  private serviceLoadingStateSubject = new BehaviorSubject<ServiceLoadingState>({
    properties: false,
    propertyGroups: false,
    contracts: false,
    loans: false,
    tenants: false,
    expenses: false,
    globalIndex: false,
    financialCalculations: false
  });
  
  // Metrics and monitoring
  private metricsSubject = new BehaviorSubject<DataCoordinationMetrics>({
    totalServices: 8,
    loadedServices: 0,
    failedServices: [],
    loadingStartTime: Date.now()
  });

  // Public observables
  public isInitializing$ = this.isInitializingSubject.asObservable();
  public servicesLoaded$ = this.servicesLoadedSubject.asObservable();
  public dataEnriched$ = this.dataEnrichedSubject.asObservable();
  public serviceLoadingState$ = this.serviceLoadingStateSubject.asObservable();
  public metrics$ = this.metricsSubject.asObservable();

  // Combined loading state
  public appLoadingState$: Observable<AppLoadingState> = combineLatest([
    this.isInitializing$,
    this.servicesLoaded$,
    this.dataEnriched$
  ]).pipe(
    map(([isInitializing, servicesLoaded, dataEnriched]) => ({
      isInitializing,
      servicesLoaded,
      dataEnriched,
      readyToDisplay: !isInitializing && servicesLoaded && dataEnriched
    })),
    distinctUntilChanged((prev, curr) => 
      prev.isInitializing === curr.isInitializing &&
      prev.servicesLoaded === curr.servicesLoaded &&
      prev.dataEnriched === curr.dataEnriched &&
      prev.readyToDisplay === curr.readyToDisplay
    ),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  // Convenience observable for components
  public readyToDisplay$ = this.appLoadingState$.pipe(
    map(state => state.readyToDisplay),
    distinctUntilChanged()
  );

  /**
   * Mark that app initialization has started
   */
  startInitialization(): void {
    // console.log('🚀 AppCoordination: Starting app initialization...');
    this.isInitializingSubject.next(true);
    this.servicesLoadedSubject.next(false);
    this.dataEnrichedSubject.next(false);
  }

  /**
   * Mark that all services have been loaded
   */
  markServicesLoaded(): void {
    // console.log('✅ AppCoordination: Services loaded');
    this.servicesLoadedSubject.next(true);
  }

  /**
   * Mark that data enrichment is complete
   */
  markDataEnriched(): void {
    // Only emit if not already enriched to prevent loops
    if (!this.dataEnrichedSubject.value) {
      // console.log('✅ AppCoordination: Data enriched');
      this.dataEnrichedSubject.next(true);
    }
  }

  /**
   * Mark that initialization is complete
   */
  completeInitialization(): void {
    // console.log('🏁 AppCoordination: Initialization complete - ready to display');
    this.isInitializingSubject.next(false);
    
    // Ensure all states are properly set
    if (!this.servicesLoadedSubject.value) {
      this.servicesLoadedSubject.next(true);
    }
    if (!this.dataEnrichedSubject.value) {
      this.dataEnrichedSubject.next(true);
    }
    
    // Final state logged for debugging if needed
    // setTimeout(() => {
    //   console.log('🔍 AppCoordination: Final state check:', {
    //     isInitializing: this.isInitializingSubject.value,
    //     servicesLoaded: this.servicesLoadedSubject.value,
    //     dataEnriched: this.dataEnrichedSubject.value
    //   });
    // }, 100);
  }

  /**
   * Reset coordination state (for logout/login cycles)
   */
  reset(): void {
    // console.log('🔄 AppCoordination: Resetting state');
    this.isInitializingSubject.next(true);
    this.servicesLoadedSubject.next(false);
    this.dataEnrichedSubject.next(false);
    
    // Reset service loading states
    this.serviceLoadingStateSubject.next({
      properties: false,
      propertyGroups: false,
      contracts: false,
      loans: false,
      tenants: false,
      expenses: false,
      globalIndex: false,
      financialCalculations: false
    });
    
    // Reset metrics
    this.metricsSubject.next({
      totalServices: 8,
      loadedServices: 0,
      failedServices: [],
      loadingStartTime: Date.now()
    });
  }

  /**
   * Mark a specific service as loaded
   */
  markServiceLoaded(serviceName: keyof ServiceLoadingState): void {
    const currentState = this.serviceLoadingStateSubject.value;
    const currentMetrics = this.metricsSubject.value;
    
    if (!currentState[serviceName]) {
      const newState = { ...currentState, [serviceName]: true };
      this.serviceLoadingStateSubject.next(newState);
      
      const loadedCount = Object.values(newState).filter(Boolean).length;
      const updatedMetrics: DataCoordinationMetrics = {
        ...currentMetrics,
        loadedServices: loadedCount,
        loadingDuration: loadedCount === currentMetrics.totalServices ? 
          Date.now() - currentMetrics.loadingStartTime : undefined
      };
      
      this.metricsSubject.next(updatedMetrics);
      
      // console.log(`✅ AppCoordination: Service '${serviceName}' loaded (${loadedCount}/${currentMetrics.totalServices})`);
      
      // Check if all services are loaded
      if (loadedCount === currentMetrics.totalServices) {
        this.markServicesLoaded();
      }
    }
  }

  /**
   * Mark a specific service as failed
   */
  markServiceFailed(serviceName: keyof ServiceLoadingState, error?: string): void {
    const currentMetrics = this.metricsSubject.value;
    const failedServices = [...currentMetrics.failedServices];
    
    if (!failedServices.includes(serviceName)) {
      failedServices.push(serviceName);
      
      const updatedMetrics: DataCoordinationMetrics = {
        ...currentMetrics,
        failedServices
      };
      
      this.metricsSubject.next(updatedMetrics);
      console.warn(`❌ AppCoordination: Service '${serviceName}' failed to load:`, error);
    }
  }

  /**
   * Get current loading progress as percentage
   */
  getLoadingProgress(): Observable<number> {
    return this.metrics$.pipe(
      map(metrics => Math.round((metrics.loadedServices / metrics.totalServices) * 100)),
      distinctUntilChanged()
    );
  }

  /**
   * Check if a specific service is loaded
   */
  isServiceLoaded(serviceName: keyof ServiceLoadingState): Observable<boolean> {
    return this.serviceLoadingState$.pipe(
      map(state => state[serviceName]),
      distinctUntilChanged()
    );
  }

  /**
   * Get comprehensive loading status for debugging
   */
  getLoadingStatus(): Observable<{
    state: AppLoadingState;
    services: ServiceLoadingState;
    metrics: DataCoordinationMetrics;
  }> {
    return combineLatest([
      this.appLoadingState$,
      this.serviceLoadingState$,
      this.metrics$
    ]).pipe(
      map(([state, services, metrics]) => ({ state, services, metrics }))
    );
  }

  /**
   * Force mark data as enriched (for PropertyListStateService integration)
   */
  forceMarkDataEnriched(): void {
    this.markDataEnriched();
  }
}
